import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Paperclip, Loader2, X } from "lucide-react";

/**
 * Multi-file attachment list for a single ticket sub-activity.
 * Files: JPG / PNG / WEBP / PDF, <=10MB each.
 */
export default function TicketActivityAttachments({ ticketId, activityId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const reload = useCallback(async () => {
    if (!ticketId || !activityId) return;
    try {
      const { data } = await api.get(`/service-tickets/${ticketId}/activities/${activityId}/attachments`);
      setFiles(data || []);
    } catch (err) { /* silent */ }
  }, [ticketId, activityId]);

  useEffect(() => { reload(); }, [reload]);

  async function onPick(e) {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    try {
      for (const f of picked) {
        const form = new FormData();
        form.append("file", f);
        await api.post(`/service-tickets/${ticketId}/activities/${activityId}/attachments`, form, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Added ${picked.length} attachment${picked.length > 1 ? "s" : ""}`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function onDelete(f) {
    try {
      await api.delete(`/service-tickets/${ticketId}/activities/${activityId}/attachments/${f.id}`);
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  return (
    <div data-testid={`activity-attachments-${activityId}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="font-mono text-[9px] uppercase tracking-widest text-[#94A3B8]">Attachments · {files.length}</div>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          data-testid={`activity-attachment-add-${activityId}`}
          className="text-[10px] font-mono uppercase tracking-widest text-[#1F4E2C] inline-flex items-center gap-1 disabled:opacity-40">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />} Add
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple onChange={onPick} className="hidden" />
      {!files.length ? (
        <div className="text-[10px] font-mono text-[#94A3B8] border border-dashed border-[#DDE5D8] px-2 py-1.5">No attachments yet.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {files.map((f) => (
            <a key={f.id} href={`${backend}/api/files/${f.id}`} target="_blank" rel="noreferrer"
              data-testid={`activity-attachment-${f.id}`}
              className="group relative flex items-center gap-1 border border-[#E2E4DC] px-2 py-1 text-[10px] font-mono text-[#0F1A12]">
              <Paperclip className="w-3 h-3 flex-shrink-0" /> {f.filename.length > 18 ? f.filename.slice(0, 15) + "…" : f.filename}
              <button type="button" onClick={(e) => { e.preventDefault(); onDelete(f); }} data-testid={`activity-attachment-delete-${f.id}`}
                className="ml-1 text-red-500 opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
